import { FormEvent, forwardRef, useEffect, useMemo, useState } from 'react';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import { Input, InputProps } from '../Input';
import { Box } from '../Box';
import { ButtonProps } from '../Button';
import { LoadingButton } from '../LoadingButton';
import { InputError } from '../InputError';
import { SearchValueType } from './types';
import { Typography } from '../Typography';
import { pipe } from 'fp-ts/lib/function';
import { fromPredicate, match } from 'fp-ts/lib/Option';

type InputSearchProp = Omit<InputProps, 'name' | 'value' | 'onChange'> & {
  className?: string;
  error?: string | string[];
  helpText?: string;
  input: {
    name: string;
    value?: SearchValueType;
    onChange?: InputProps['onChange'];
  };
  label: string;
  required?: boolean;
};

type ButtonInputProp = Omit<ButtonProps, 'text' | 'onClick'> & {
  buttonText: string;
  btnOnClick: (value: string) => unknown;
  button: {
    isLoading: boolean;
    loadingLabel: string;
    disabled?: boolean;
  };
};

export type InputSearchTypeMap = BaseTypeMap<
  'input',
  InputSearchProp & ButtonInputProp
>;

export type InputSearchProps = ComponentProps<InputSearchTypeMap>;

export const InputSearch: Component<InputSearchTypeMap> = forwardRef(
  function InputSearch({ name, ...rest }, ref) {
    const [val, setVal] = useState<SearchValueType>();
    const srchBtnClick = () => rest.btnOnClick?.(val as string);
    useEffect(() => setVal(rest?.input?.value), [rest?.input?.value]);

    const inputText = useMemo(
      () =>
        pipe(
          rest,
          fromPredicate((re) => !!re?.error),
          match(
            () => (
              <Typography
                intent="label"
                className="mt-2 w-0 min-w-full"
                id={`${name}-desc`}
              >
                {rest.helpText}
              </Typography>
            ),
            () => <InputError error={rest.error} id={`${name}-desc`} />
          )
        ),
      [rest, name]
    );

    return (
      <Box className={rest?.className}>
        <Box className="flex items-end">
          <Input
            ref={ref}
            required={rest?.required}
            label={rest.label}
            type="text"
            className="flex-1"
            value={val}
            disabled={rest?.disabled}
            onChange={(e: FormEvent<HTMLInputElement>) =>
              setVal(e?.currentTarget?.value)
            }
            {...rest.input}
          />
          <LoadingButton
            className="col-span-full sm:col-start-1 md:col-span-4 md:col-start-8"
            {...rest.button}
            onClick={srchBtnClick}
          >
            {rest.buttonText}
          </LoadingButton>
        </Box>
        {inputText}
      </Box>
    );
  }
);
